








































































































































import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import LanguagesStore from "@/mixins/constants/languagesStore";
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";

// Enums
import { DataModules } from "@/store/modules/data";
import SupportedLocaleCodes from "@/locales";

// Types
import { IBrand } from "@/models/brands";
import { IClient, IClientForm } from "@/models/clients";
import { IState } from "@/store";
import { UUID } from "@/data/enums";
import { ILanguage } from "@/models/constants";

@Component({
  components: {
    "notifications-disabled-checkbox": () =>
      import("@/components/app/global/fields/notificationsDisabledCheckbox.vue")
  },
  computed: {
    ...mapState({
      brand: (state: IState) => state.brand,

      brandSettingsScope(state: IState, getters) {
        return getters[`data/${DataModules.BRANDS}/brandScope`](
          this.clientBrandId
        );
      },
      brandSettings(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.BRANDS,
          scope: this.brandSettingsScope
        });
      }
    })
  }
})
export default class ClientProfileBasicConfigurationForm extends Mixins(
  Context,
  HasValidation,
  LanguagesStore,
  RequestPromises
) {
  // Map State

  public brand!: IBrand;
  public brandSettingsScope!: string;
  public brandSettings!: IBrand;

  // Props

  @Prop({ required: true })
  public client!: IClient;

  @Prop({ default: false })
  public isSticky!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  // Data

  public form = {} as IClientForm;

  public isLoading = true;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  public sendingVerificationEmail = false;
  public isDeleteProcessing = false;

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;
    this.loadData();
  }

  public beforeDestroy() {
    if (this.isOrgMode)
      this.$store.dispatch("data/binSingle", {
        storeModule: DataModules.BRANDS,
        scope: this.brandSettingsScope,
        vm: this
      });
  }

  // Methods

  public async loadData() {
    try {
      if (!this.isOrgMode && this.brand.id !== this.clientBrandId) {
        this.$store.dispatch("toast/show", {
          message: this.$t("_.client_from_different_brand")
        });
        return this.$router.push({ name: "adminClients" });
      }

      await this.getBrandSettings();

      this.form = this.initForm();
      this.requestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public async getBrandSettings() {
    if (!this.isOrgMode) return Promise.resolve();

    return this.$store.dispatch(`data/${DataModules.BRANDS}/getBrandSettings`, {
      id: this.clientBrandId,
      scope: this.brandSettingsScope,
      params: {
        with: "languages"
      },
      ignoreStored: this.isReloading,
      vm: this
    });
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public initForm() {
    return _.pick(this.client, [
      "firstname",
      "lastname",
      "interface_language_id",
      "email",
      "notifications_disabled"
    ]);
  }

  public async validateBeforeSubmit() {
    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      this.isProcessing = true;

      await this.updateClient();

      if (
        this.isClient &&
        this.form.interface_language_id !== this.clientLanguageId
      ) {
        await this.setClientLanguage();
        await this.$store.dispatch("auth/client/getUser");
      }

      this.$emit("refreshClient");

      if (!this.isSticky) {
        this.$store.dispatch("toast/show", {
          message: this.$t("_sentence.client_details_updated")
        });
      }
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public revertToInitialState() {
    this.form = this.initForm();
  }

  public updateClient() {
    if (_.isEmpty(this.formValues)) return Promise.resolve();

    return this.$store.dispatch(`data/${DataModules.CLIENTS}/update`, {
      id: this.client.id,
      data: this.formValues
    });
  }

  public setClientLanguage() {
    return this.$store.dispatch(
      "i18n/setLanguage",
      this.getLanguageCode(this.form.interface_language_id as ILanguage["id"])
    );
  }

  public async resendVerification() {
    if (!this.$userCan("update_client")) {
      return;
    }
    this.sendingVerificationEmail = true;

    try {
      await this.$store.dispatch(
        `data/${DataModules.CLIENTS}/resendVerificationEmail`,
        { id: this.client.id }
      );

      this.$buefy.toast.open({
        message: this.$t("_.verification_email_sent") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }

    this.sendingVerificationEmail = false;
  }

  public getLanguageCode(languageId: ILanguage["id"]) {
    return (
      _.find(this.languages, ["id", languageId])?.code ||
      SupportedLocaleCodes.EN
    );
  }

  // Computed

  public get clientBrandId(): IBrand["id"] {
    return this.client.brand_id || "";
  }

  public get clientLanguageId(): ILanguage["id"] {
    return this.client.interface_language_id || "";
  }

  public get isOrgMode() {
    return this.brand.id === UUID.ORG;
  }

  public get brandLanguages() {
    return _.orderBy(
      this.brandSettings?.languages || this.brand.languages,
      ["language"],
      ["asc"]
    );
  }

  public get langDoesNotExist() {
    return !_.some(this.brandLanguages, ["id", this.clientLanguageId]);
  }

  public get missingLanguageName() {
    return (
      _.find(this.languages, ["id", this.clientLanguageId])?.language || ""
    );
  }

  public get clientIsNotVerified() {
    return (
      _.get(this.client, "has_login") && !_.get(this.client, "verified", true)
    );
  }

  public get isStaged() {
    return !!this.client?.staged_import;
  }

  public get formIsChanged() {
    return !this.$_.isEqual(this.form, this.initForm());
  }

  public get formValues() {
    const differences = _.omitBy(this.form, (v, k) => {
      return this.initForm()[k] === v;
    });

    if (this.form.interface_language_id !== this.clientLanguageId)
      differences.document_language_id = this.form.interface_language_id;

    return differences;
  }

  // Guarding

  public get guardFunctionalities() {
    return ["get_client"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get canUpdate() {
    return this.$userCan("update_client");
  }

  // Watchers

  @Watch("client")
  public onClientDataChange() {
    this.form = this.initForm();
  }

  @Watch("formIsChanged")
  public onIsEnabledChange() {
    this.$emit("isEnabled", this.formIsChanged);
  }

  @Watch("isProcessing")
  public onIsProcessingChange() {
    this.$emit("isProcessing", this.isProcessing);

    if (!this.isProcessing && !this.hasErrors) {
      this.$emit("isEnabled", false);
    }
  }
}
